import { IonAvatar, IonIcon, IonImg, useIonToast } from "@ionic/react";
import { motion } from 'framer-motion';
import React, { useContext, useMemo, useState } from "react";
import { useHistory } from "react-router";
import { AppContext } from "../../../AppStateProvider";
import { default as biskLogo, default as biskSvg } from "../../../assets/icons/bisk/BISK_ICON_NO_DEGREE.svg";
import useAuthService from "../../../hooks/useAuthService";
import { EnrichedBoardResult } from "../../../hooks/useBoardResults";
import { formatAmount } from "../../../services/utils";
import UsernameDisplay from "../../ComponentsUI/Username/UsernameDisplay";


interface ContainerProps {
    result: EnrichedBoardResult;
    blurred?: boolean;
    isAuthed?: boolean;
}

const BoardUserBiskCard: React.FC<ContainerProps> = React.memo(({ result, blurred, isAuthed }) => {
    const history = useHistory();
    const { state } = useContext(AppContext);
    const { login } = useAuthService();
    const [present] = useIonToast();

    const [isFlipped, setIsFlipped] = useState<boolean>(false);

    // Use pre-computed display values from the enriched result
    const {
        display_username: originUsername,
        display_profile_pic: originProfilePic,
        number: amount,
    } = result;

    const blurredClass = useMemo(() => blurred ? 'blur-lg' : '', [blurred]);

    const presentToastLoginHandler = async (message: string) => {
        await present({
            message: message,
            duration: 3000,
            position: 'top',
            icon: biskSvg,
            buttons: [
                {
                    text: 'Login',
                    handler: async () => {
                        await login();
                    }
                },
                {
                    text: 'Cancel',
                    role: 'cancel'
                }
            ]
        });
    };

    const onClickUsername = async (username: string) => {
        if (!isAuthed) {
            await presentToastLoginHandler("You must be logged in to view profiles!");
            return;
        }

        history.push('/profile/' + username);
    }

    const onFlip = () => {
        setIsFlipped(!isFlipped);
    };

    // No loading state needed - data is pre-fetched
    if (!originUsername) {
        return null; // Gracefully handle edge case of missing data
    }

    return (
        <div
            className={"flex flex-col border-b border-gray-900 " + blurredClass}
            style={{ minHeight: '72px' }} // Fixed min-height prevents layout shifts
        >
            <div className="flex items-center justify-between px-4 py-3">
                <div className="flex items-center space-x-3 cursor-pointer">
                    <IonAvatar slot="start" className="mx-0.5"
                        onClick={() => onClickUsername(originUsername)}>
                        <IonImg className="shrink w-12 h-12 flex-none rounded-full" src={originProfilePic}></IonImg>
                    </IonAvatar>
                    <div>
                        <UsernameDisplay className="font-semibold" username={originUsername} userId={result.id}
                            onUsernameClickHandler={onClickUsername} loggedInId={state.user.id} />
                    </div>
                </div>
                <motion.div
                    className="flex flex-none items-center justify-center w-10 h-10 bg-highlight rounded-full cursor-pointer"
                    animate={{ rotateY: isFlipped ? 180 : 0 }}
                    transition={{ duration: 0.25 }}
                    onClick={onFlip}
                >
                    {isFlipped ? (
                        <motion.div
                            style={{ rotateY: "180deg" }}
                        >
                            <IonIcon src={biskLogo} size="large" style={{ rotateY: '180deg' }}
                                className="w-8 h-8 pt-2" />
                        </motion.div>
                    ) : (
                        <motion.span
                            className="text-dark text-xl font-semibold"
                        >
                            {formatAmount(amount)}
                        </motion.span>
                    )}
                </motion.div>
            </div>
        </div>
    );
});

export default BoardUserBiskCard;